import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useMockRoundStore } from 'e2e-tests/mocks/mockRoundStore';

import { IS_STREAM_GAMES_PAGE } from 'shared/constants/constants';
import {
  MovesEnum,
  RoomStatusEnum,
  RoundOutcome,
  RoundStagesEnum,
  RoundWinnerOutcomes,
  GameStagesEnum,
} from 'shared/constants/shared-enums';
import contractAbi from 'shared/contract/contract.abi.json';
import { useAccount, usePublicClient } from 'shared/hooks/use-web3';
import { useQuestsStore } from 'shared/store/questsStore';
import { useNewRoomStore } from 'shared/store/room-store';
import { useRoomStore } from 'shared/store/roomStore';
import { isMocked } from 'shared/utils/isMocked';
import { normalizeRounds } from 'shared/utils/normalizeData';

export type RoundItem = {
  moveA: MovesEnum;
  moveB: MovesEnum;
  encrMoveA: `0x${string}`;
  encrMoveB: `0x${string}`;
  winner: RoundWinnerOutcomes;
  stage: GameStagesEnum;
  roundStage: RoundStagesEnum;
};

const asArray = (val: unknown): RoundItem[] => (Array.isArray(val) ? val : []);

export const useGetHistoryMoves = (currentRound?: number, initForStreams?: boolean) => {
  const [data, setData] = useState<RoundItem[]>([]);
  const { address, isConnected } = useAccount();
  const { activeRoomId } = useParams();

  const roomIdBig = BigInt(activeRoomId ? activeRoomId : 1);

  const publicClientForUrl = usePublicClient();

  const { roundCounter, roomStatus, amIPlayerA, setLastRoundOutcome, setLastMoves } =
    useRoomStore();
  const { fetchUserBalances } = useQuestsStore();
  // ! Temporary use both room stores (roomStatus), until removing old roomStore.
  const { room } = useNewRoomStore();

  const publicClient = usePublicClient();

  const fetchData = useCallback(async () => {
    if (!publicClient) {
      setData([]);
      return;
    }
    try {
      const res = await publicClient.readContract({
        address: import.meta.env.VITE_RSP_CONTRACT_ADDRESS,
        abi: contractAbi,
        functionName: 'getAllRoomRounds',
        args: [roomIdBig],
        authorizationList: undefined,
      });
      setData(asArray(res));
    } catch {
      setData([]);
    }
  }, [publicClient, roomIdBig]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  if (isMocked && !IS_STREAM_GAMES_PAGE) {
    const { rounds } = useMockRoundStore.getState();
    return rounds.slice().reverse();
  }

  const isGuestMode =
    !isConnected && typeof window !== 'undefined' && !!(window as any).__STREAM_META__;

  const historyMoves = useMemo<RoundItem[]>(() => {
    if (isGuestMode) {
      const meta = (window as any).__STREAM_META__;
      return asArray(normalizeRounds(meta));
    }
    return asArray(data);
  }, [isGuestMode, data]);

  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      void fetchData();
      if (publicClientForUrl && address) {
        void fetchUserBalances(publicClientForUrl, address);
      }
    }
  }, [roundCounter, currentRound, publicClientForUrl, address, fetchData, fetchUserBalances]);

  useEffect(() => {
    const status = initForStreams ? room.roomStatus : roomStatus;
    if (status === RoomStatusEnum.Closed) {
      void fetchData();
    }
  }, [roomStatus, room.roomStatus, fetchData, initForStreams, room.roomStatus]);

  useEffect(() => {
    const status = initForStreams ? room.roomStatus : roomStatus;
    if (!status) return;

    const roomClosed = status === RoomStatusEnum.Closed;
    const idx = roomClosed ? historyMoves.length - 1 : historyMoves.length - 2;

    const hasWinner = idx >= 0 && Boolean((historyMoves as any)[idx]?.winner);
    if (!hasWinner) return;

    let outcome = RoundOutcome.Lose;
    const winner = (historyMoves as any)[idx].winner;

    if (amIPlayerA && winner === RoundWinnerOutcomes.PlayerA) outcome = RoundOutcome.Win;
    if (!amIPlayerA && winner === RoundWinnerOutcomes.PlayerB) outcome = RoundOutcome.Win;
    if (winner === RoundWinnerOutcomes.Draw) outcome = RoundOutcome.Draw;

    setLastRoundOutcome(outcome);
    setLastMoves({
      round: roomClosed ? historyMoves.length : historyMoves.length - 1,
      playerAMove: (historyMoves as any)[idx]?.moveA,
      playerBMove: (historyMoves as any)[idx]?.moveB,
    });
  }, [
    historyMoves,
    roomStatus,
    room.roomStatus,
    amIPlayerA,
    setLastRoundOutcome,
    setLastMoves,
    initForStreams,
    room.roomStatus,
  ]);

  return historyMoves.length ? [...historyMoves].reverse() : [];
};
